import { useQuery } from "@apollo/client"
import {
  Box,
  Button,
  Container,
  Field,
  HStack,
  Heading,
  Input,
  NativeSelect,
  RadioGroup,
  Separator,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react"
import { useState } from "react"
import {
  CoverPageOfferFragment,
  CoverPageResourceDocument,
  OfferAccessState,
  OfferKind,
  OfferPriceOption,
  OfferScope,
  useCoverPageOffersQuery
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { flattenEdges } from "../../utils/apollo"
import { getResourceSlugId } from "../../utils/urls"
import CoverPage from "../CoverPage"
import CoverPageOfferCard from "../CoverPageOffer"
import { useCoverPageContext } from "../CoverPageProvider"
import { offerHasMeta, offerHasPriceDescription } from "../utils"
import { offerPermutations } from "./offerSandboxData"

import Link, { LinkTo } from "@pathwright/ux/src/components/link/Link"

// Permutations covering all scenarios from the "Offer Block in 3 Parts" screenshot
const permutations: { label: string; offer: CoverPageOfferFragment }[] =
  offerPermutations

// Default custom offer for the form (new API shape)
const defaultCustomOffer: CoverPageOfferFragment = {
  id: Date.now(),
  school_id: 999_999,
  cover_page_id: 1,
  kind: OfferKind.JoinCohort,
  cohort_id: Date.now(),
  resource_license_offering_id: null,
  scope: OfferScope.Member,
  title: "Custom Offer",
  description: "A custom offer for testing",
  cta_label: "Join",
  external_url: null,
  position: null,
  price_options: [
    {
      option_label: "Free",
      button_label: "Join",
      price_description: ""
    }
  ],
  access: {
    meta: [],
    state: OfferAccessState.Available
  }
}

type CoverPageOfferSandboxProps = {
  resourceId?: number
  backTo?: LinkTo
}

const CoverPageOfferSandbox = ({
  resourceId,
  backTo
}: CoverPageOfferSandboxProps) => {
  const { data: offerData } = useCoverPageOffersQuery({
    variables: { context: { resource_id: resourceId! }, first: 100 },
    skip: !resourceId
  })

  const { data: resourceData } = useQuery(CoverPageResourceDocument, {
    variables: { id: resourceId },
    skip: !resourceId
  })

  const resource = resourceData?.resource

  const offers = flattenEdges(offerData?.offerConnections) || []

  // Modes: resource (if offers), sample, custom
  const hasResourceOffers = offers.length > 0
  const initialMode = hasResourceOffers ? "resource" : "sample"
  const [mode, setMode] = useState<"resource" | "sample" | "custom">(
    initialMode
  )
  const [selectedResourceOffer, setSelectedResourceOffer] = useState(0)
  const [selectedPermutation, setSelectedPermutation] = useState(0)

  // Always auto-generate id for custom offer
  const [customOffer, setCustomOffer] = useState<CoverPageOfferFragment>({
    ...defaultCustomOffer,
    id: Date.now()
  })

  const selectedSampleOffer = permutations[selectedPermutation].offer
  const selectedResourceOfferObj = offers[selectedResourceOffer]

  const currentOffer =
    mode === "resource" && !!resourceId
      ? selectedResourceOfferObj
      : mode === "sample"
      ? selectedSampleOffer
      : customOffer

  // When updating, always set a new id
  const updateCustomOffer = (updates: Partial<CoverPageOfferFragment>) => {
    setCustomOffer((prev) => ({ ...prev, ...updates, id: Date.now() }))
  }

  const updatePriceOption = (
    index: number,
    updates: Partial<OfferPriceOption>
  ) => {
    setCustomOffer((prev) => ({
      ...prev,
      price_options: prev.price_options.map((option, i) =>
        i === index && option ? { ...option, ...updates } : option
      )
    }))
  }

  const addPriceOption = () => {
    setCustomOffer((prev) => ({
      ...prev,
      price_options: [
        ...(prev.price_options || []),
        {
          option_label: "",
          button_label: "Join",
          price_description: ""
        }
      ]
    }))
  }

  const removePriceOption = (index: number) => {
    setCustomOffer((prev) => ({
      ...prev,
      price_options: prev.price_options.filter((_, i) => i !== index)
    }))
  }

  const updateAccessMeta = (index: number, value: string) => {
    setCustomOffer((prev) => ({
      ...prev,
      access: {
        ...prev.access,
        meta: prev.access.meta.map((meta, i) => (i === index ? value : meta))
      }
    }))
  }

  const addAccessMeta = () => {
    setCustomOffer((prev) => ({
      ...prev,
      access: {
        ...prev.access,
        meta: [...prev.access.meta, ""]
      }
    }))
  }

  const removeAccessMeta = (index: number) => {
    setCustomOffer((prev) => ({
      ...prev,
      access: {
        ...prev.access,
        meta: prev.access.meta.filter((_, i) => i !== index)
      }
    }))
  }

  return (
    <Container maxW="container.xl" p={8} bgColor="gray.50">
      <VStack gap={4} align="stretch">
        <Box>
          <Heading size="lg" mt={0} mb={4}>
            Cover Page Offer Sandbox
          </Heading>
          <Text color="gray.700">Test offer configurations and states</Text>
        </Box>

        <Box>
          <Heading size="md" mb={2}>
            Select a Mode
          </Heading>

          <RadioGroup.Root
            value={mode}
            onValueChange={({ value }) =>
              setMode(value as "resource" | "sample" | "custom")
            }
            mb={2}
          >
            <HStack gap={4}>
              {hasResourceOffers && (
                <RadioGroup.Item value="resource">
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Resource Offers</RadioGroup.ItemText>
                </RadioGroup.Item>
              )}
              <RadioGroup.Item value="sample">
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText>Sample Offers</RadioGroup.ItemText>
              </RadioGroup.Item>
              <RadioGroup.Item value="custom">
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText>Custom Offer</RadioGroup.ItemText>
              </RadioGroup.Item>
            </HStack>
          </RadioGroup.Root>
          <Text fontSize="sm" m={0} fontStyle="italic">
            {mode === "resource"
              ? "Select a real-data offering for the current Resource from the dropdown to the right. Note: currently these are being transformed from legacy offerings to match the Offer shape."
              : mode === "sample"
              ? "Select a sample Offer from the dropdown to the right."
              : "Use the form to create a custom Offer."}
          </Text>
        </Box>

        <HStack gap={8} align="flex-start">
          {/* Left side - Offer Card Preview */}
          <Box flex="0 0 auto" w="400px">
            <Heading size="md" mb={4}>
              Offer Preview
            </Heading>
            {currentOffer ? (
              <Box maxW="328px" bgColor="gray.400" p={6} borderRadius="lg">
                <CoverPageOfferCard
                  offer={currentOffer}
                  displayMeta={offerHasMeta(currentOffer)}
                  displayDescription={offerHasPriceDescription(currentOffer)}
                  showInspectOffering={
                    mode === "resource" &&
                    typeof window !== "undefined" &&
                    (window as any).user.is_superuser
                  }
                />
              </Box>
            ) : (
              <Text color="gray.500">No offer to display</Text>
            )}
          </Box>

          {/* Right side - Controls Sidebar */}
          <Box flex="1" minW="0" maxW="500px">
            {mode === "resource" && hasResourceOffers && (
              <Box
                bg="white"
                border="1px"
                borderColor="gray.200"
                borderRadius="lg"
                p={6}
                shadow="sm"
              >
                <Heading size="md" mb={4}>
                  Resource Offers
                </Heading>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={selectedResourceOffer}
                    onChange={(e) =>
                      setSelectedResourceOffer(Number(e.target.value))
                    }
                    mb={4}
                  >
                    {offers.map(
                      (offer: CoverPageOfferFragment, index: number) => (
                        <option key={offer.id} value={index}>
                          {offer.title || `Offer #${offer.id}`}
                        </option>
                      )
                    )}
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </Box>
            )}
            {mode === "sample" && (
              <Box
                bg="white"
                border="1px"
                borderColor="gray.200"
                borderRadius="lg"
                p={6}
                shadow="sm"
              >
                <Heading size="md" mb={4}>
                  Sample Offers
                </Heading>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={selectedPermutation}
                    onChange={(e) =>
                      setSelectedPermutation(Number(e.target.value))
                    }
                    mb={4}
                  >
                    {permutations.map((permutation, index) => (
                      <option key={index} value={index}>
                        {permutation.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </Box>
            )}

            {mode === "custom" && (
              <Box
                bg="white"
                border="1px"
                borderColor="gray.200"
                borderRadius="lg"
                p={6}
                shadow="sm"
              >
                <Heading size="md" mb={4}>
                  Custom Offer Configuration
                </Heading>
                <VStack gap={4} align="stretch">
                  {/* ID field removed, auto-generated */}
                  {/*
                  <Field.Root>
                    <Field.Label>ID</Field.Label>
                    <NumberInput
                      value={customOffer.id}
                      onChange={(_, value) => updateCustomOffer({ id: value })}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </Field.Root>
                  */}

                  <Field.Root>
                    <Field.Label>Title</Field.Label>
                    <Input
                      value={customOffer.title}
                      onChange={(e) =>
                        updateCustomOffer({ title: e.target.value })
                      }
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Description</Field.Label>
                    <Textarea
                      value={customOffer.description ?? ""}
                      onChange={(e) =>
                        updateCustomOffer({ description: e.target.value })
                      }
                    />
                  </Field.Root>

                  {/*
                  <Field.Root>
                    <Field.Label>Order</Field.Label>
                    <NumberInput
                      value={customOffer.order || 0}
                      onChange={(_, value) =>
                        updateCustomOffer({ order: value })
                      }
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </Field.Root>
                  */}

                  <Separator
                    borderBottomWidth={0}
                    borderTopWidth="1px"
                    w="full"
                  />

                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Heading size="sm">Price Options</Heading>
                      <Button size="sm" onClick={addPriceOption}>
                        Add Option
                      </Button>
                    </HStack>
                    {customOffer.price_options.map((option, index) => (
                      <Box
                        key={index}
                        p={4}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        mb={2}
                      >
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="medium">Option {index + 1}</Text>
                          <Button
                            size="sm"
                            colorPalette="red"
                            onClick={() => removePriceOption(index)}
                          >
                            Remove
                          </Button>
                        </HStack>
                        <VStack gap={2} align="stretch">
                          <Field.Root>
                            <Field.Label>Option Label</Field.Label>
                            <Input
                              value={option.option_label}
                              onChange={(e) =>
                                updatePriceOption(index, {
                                  option_label: e.target.value || ""
                                })
                              }
                            />
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>Button Label</Field.Label>
                            <Input
                              value={option.button_label}
                              onChange={(e) =>
                                updatePriceOption(index, {
                                  button_label: e.target.value
                                })
                              }
                            />
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>Price Description</Field.Label>
                            <Input
                              value={option.price_description}
                              onChange={(e) =>
                                updatePriceOption(index, {
                                  price_description: e.target.value
                                })
                              }
                            />
                          </Field.Root>
                        </VStack>
                      </Box>
                    ))}
                  </Box>

                  <Separator
                    borderBottomWidth={0}
                    borderTopWidth="1px"
                    w="full"
                  />

                  <Box>
                    <Field.Root>
                      <Field.Label>Access State</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={customOffer.access.state}
                          onChange={(e) => {
                            if (customOffer.access) {
                              updateCustomOffer({
                                access: {
                                  ...customOffer.access,
                                  state: e.target.value as OfferAccessState
                                }
                              })
                            }
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="available">Available</option>
                          <option value="unavailable">Unavailable</option>
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>

                    <Box mt={4}>
                      <HStack justify="space-between" mb={2}>
                        <Heading size="sm">Access Meta</Heading>
                        <Button size="sm" onClick={addAccessMeta}>
                          Add Meta
                        </Button>
                      </HStack>
                      {customOffer.access.meta.map((meta, index) =>
                        meta ? (
                          <HStack key={index} mb={2}>
                            <Input
                              value={meta}
                              onChange={(e) =>
                                updateAccessMeta(index, e.target.value)
                              }
                              placeholder="Meta information"
                            />
                            <Button
                              size="sm"
                              colorPalette="red"
                              onClick={() => removeAccessMeta(index)}
                            >
                              Remove
                            </Button>
                          </HStack>
                        ) : null
                      )}
                    </Box>
                  </Box>
                </VStack>
              </Box>
            )}
          </Box>
        </HStack>
      </VStack>
    </Container>
  )
}

const BackToCoverPageLink = () => {
  return (
    <Box position="absolute" top="1rem" right="1rem" zIndex={10}>
      <Link
        relativeTo="../"
        color="blue.500"
        fontSize="sm"
        fontWeight="medium"
        _hover={{ textDecoration: "underline" }}
        _before={{
          content: '"<— "'
        }}
      >
        Back to Cover Page
      </Link>
    </Box>
  )
}

export const CoverPageOfferSandboxImmersive = () => {
  const resourceId = useCoverPageContext((v) => v.resource.id)
  const { me } = usePathwrightContext()

  if (!me?.permissions?.has_pathwright_staff_access) {
    return null
  }

  return <CoverPageOfferSandbox resourceId={resourceId} />
}

// Offers wrapper component (for testing)
export const OffersWrapper = ({ match }: { match: any }) => {
  const { resourceSlug } = match.params
  const resourceId = getResourceSlugId(resourceSlug)

  return (
    <>
      <CoverPage
        resourceId={resourceId as number}
        closeTarget="/library/"
        baseCoverRoute={`/${resourceSlug}/cover/`}
      />
      <BackToCoverPageLink />
      <CoverPageOfferSandbox
        resourceId={resourceId as number}
        backTo={`/${resourceSlug}/cover/`}
      />
    </>
  )
}

export default CoverPageOfferSandbox
